import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { serverApi } from '../../api';
import { EmpresaFormFields, normalizarVoz } from '../../components/EmpresaFormFields';
import { guardarColoresStorage } from '../../helpers/theme';

const MySwal = withReactContent(Swal);

export const EditarEmpresaModal = ({ show, onClose, onSuccess, empresa, isSuperAdmin = true }) => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        codigoEntidad: '',
        nombreEntidad: '',
        nit: '',
        imagenLogo: '',
        imagenIcono: '',
        llamadoVoz: 0,
        voz: normalizarVoz('es-CO'),
        textoMarquee: '',
        colorPrimario: 'var(--color-primario)',
        colorSecundario: 'var(--color-secundario)',
        telefonoContacto: '',
        emailContacto: '',
        direccion: '',
        ciudad: '',
        maxSedes: 1,
        indActivo: 1,
    });

    useEffect(() => {
        if (empresa) {
            setForm({
                codigoEntidad: empresa.codigoEntidad || '',
                nombreEntidad: empresa.nombreEntidad || '',
                nit: empresa.nit || '',
                imagenLogo: empresa.imagenLogo || '',
                imagenIcono: empresa.imagenIcono || '',
                llamadoVoz: empresa.llamadoVoz ?? 0,
                voz: normalizarVoz(empresa.voz),
                textoMarquee: empresa.textoMarquee || '',
                colorPrimario: empresa.colorPrimario || 'var(--color-primario)',
                colorSecundario: empresa.colorSecundario || 'var(--color-secundario)',
                telefonoContacto: empresa.telefonoContacto || '',
                emailContacto: empresa.emailContacto || '',
                direccion: empresa.direccion || '',
                ciudad: empresa.ciudad || '',
                maxSedes: empresa.maxSedes || 1,
                indActivo: empresa.indActivo ?? 1,
            });
        }
    }, [empresa]);

    if (!show || !empresa) return null;

    const handleChange = (e) => {
        const { id, value, type } = e.target;
        setForm(prev => ({
            ...prev,
            [id]: type === 'number' || id === 'llamadoVoz' || id === 'maxSedes' || id === 'indActivo'
                ? Number(value) : value,
        }));
    };

    const handleImageChange = (changes) => {
        setForm(prev => ({ ...prev, ...changes }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombreEntidad) {
            return MySwal.fire('Atención', 'El nombre de la empresa es obligatorio', 'warning');
        }
        try {
            setLoading(true);
            const resp = await serverApi.put('/superadmin/entidades', form);
            if (resp.data.ok) {
                // Actualizar localStorage si el usuario actual pertenece a esta entidad
                const rawUser = localStorage.getItem('usuario');
                if (rawUser) {
                    try {
                        const parsed = JSON.parse(rawUser);
                        if (parsed.codigoEntidad === form.codigoEntidad) {
                            localStorage.setItem('usuario', JSON.stringify({
                                ...parsed,
                                nombreEntidad: form.nombreEntidad,
                                imagenLogo: form.imagenLogo,
                                imagenIcono: form.imagenIcono,
                                llamadoVoz: form.llamadoVoz,
                            }));
                            // Si se editó la empresa del propio usuario, refrescar el tema
                            guardarColoresStorage(form.colorPrimario, form.colorSecundario);
                        }
                    } catch (_) { }
                }
                MySwal.fire({
                    title: '¡Empresa Actualizada!',
                    text: resp.data.msg,
                    icon: 'success',
                    timer: 1800,
                    showConfirmButton: false,
                });
                if (onSuccess) onSuccess();
                if (onClose) onClose();
            }
        } catch (error) {
            const msg = error.response?.data?.msg || 'Error al actualizar la empresa';
            MySwal.fire('Error', msg, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: 20, fontFamily: "'Inter', Arial, sans-serif",
        }}>
            <div style={{
                background: '#ffffff', borderRadius: 24, width: '100%',
                maxWidth: 700, maxHeight: '92vh', overflowY: 'auto',
                boxShadow: '0 25px 60px rgba(0,0,0,0.25)', border: '1px solid #e2e8f0',
            }}>
                {/* Header */}
                <div style={{
                    background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                    padding: '24px 28px', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    position: 'sticky', top: 0, zIndex: 1,
                }}>
                    <div>
                        <h4 style={{ margin: 0, fontWeight: 800, fontSize: 20 }}>✏️ Editar Empresa</h4>
                        <p style={{ margin: '4px 0 0', opacity: 0.85, fontSize: 13 }}>
                            {form.codigoEntidad} — {form.nombreEntidad}
                        </p>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white',
                        width: 36, height: 36, borderRadius: '50%', fontSize: 18, cursor: 'pointer',
                    }}>✕</button>
                </div>

                <form onSubmit={handleSubmit} style={{ padding: 28 }}>
                    {/* ── Campos comunes compartidos ── */}
                    <EmpresaFormFields
                        form={form}
                        onChange={handleChange}
                        onImageChange={handleImageChange}
                        isSuperAdmin={isSuperAdmin}
                        readonlyCodigo={true}
                        accentColor="#1e40af"
                    />

                    {/* Botones */}
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 8 }}>
                        <button type="button" onClick={onClose} style={{
                            padding: '12px 20px', borderRadius: 12, border: '1.5px solid #cbd5e1',
                            background: 'white', color: '#475569', fontWeight: 700, cursor: 'pointer',
                        }}>Cancelar</button>
                        <button type="submit" disabled={loading} style={{
                            padding: '12px 28px', borderRadius: 12, border: 'none',
                            background: loading ? '#94a3b8' : 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
                            color: 'white', fontWeight: 800, cursor: loading ? 'not-allowed' : 'pointer',
                            boxShadow: loading ? 'none' : '0 4px 14px rgba(30,64,175,0.3)',
                        }}>
                            {loading ? 'Guardando...' : '💾 GUARDAR CAMBIOS'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
